// V9.23 · Categorias (plano de contas) e centros de custo — sementes editáveis
import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Sparkles } from 'lucide-react';
import api from '@/lib/api';

const TIPOS = ['RECEITA', 'DEDUCAO', 'DESPESA'];
const EMPTY_CONTA = { cod_conta: '', tipo: 'DESPESA', descricao: '', cc_padrao: 'OPE', classe: 'variavel' };

export const CategoriasTab = ({ planoContas = [], centrosCusto = [], onReload }) => {
  const [filtroTipo, setFiltroTipo] = useState('DESPESA');
  const [filtroClasse, setFiltroClasse] = useState('todas');
  const [q, setQ] = useState('');
  const [openConta, setOpenConta] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [formConta, setFormConta] = useState(EMPTY_CONTA);
  const [openCC, setOpenCC] = useState(false);
  const [editingCC, setEditingCC] = useState(null);
  const [formCC, setFormCC] = useState({ cod_cc: '', nome_cc: '' });

  const lista = useMemo(() => planoContas.filter((c) => {
    if (c.tipo !== filtroTipo) return false;
    if (filtroTipo === 'DESPESA' && filtroClasse !== 'todas' && (c.classe || 'variavel') !== filtroClasse) return false;
    if (q && !`${c.cod_conta} ${c.descricao}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [planoContas, filtroTipo, filtroClasse, q]);

  const salvarConta = async () => {
    if (!formConta.cod_conta.trim() || !formConta.descricao.trim())
      return toast.error('Informe o código e a descrição.');
    try {
      const body = { ...formConta, classe: formConta.tipo === 'DESPESA' ? formConta.classe : null };
      if (editingConta) await api.put(`/fin/plano-contas/${editingConta.id}`, body);
      else await api.post('/fin/plano-contas', body);
      toast.success('Categoria salva.');
      setOpenConta(false); onReload?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar categoria.'); }
  };

  const excluirConta = async (c) => {
    try {
      await api.delete(`/fin/plano-contas/${c.id}`);
      toast.success('Categoria excluída.'); onReload?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao excluir.'); }
  };

  const salvarCC = async () => {
    if (!formCC.cod_cc.trim() || !formCC.nome_cc.trim()) return toast.error('Informe código e nome.');
    try {
      if (editingCC) await api.put(`/fin/centros-custo/${editingCC.id}`, formCC);
      else await api.post('/fin/centros-custo', formCC);
      toast.success('Centro de custo salvo.');
      setOpenCC(false); onReload?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar.'); }
  };

  const semear = async () => {
    try {
      await api.post('/fin/seed').catch(() => null);
      const { data } = await api.post('/fin/ledger/seed-extras');
      toast.success(`Sementes aplicadas · ${data.contas_criadas} contas, ${data.centros_custo_criados} CC, ${data.despesas_classificadas} classificadas.`);
      onReload?.();
    } catch (err) { toast.error('Erro ao aplicar sementes.'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tab-categorias">
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-lg">Categorias · plano de contas</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={semear} data-testid="cat-semear">
              <Sparkles className="w-4 h-4 mr-1" /> Aplicar sementes
            </Button>
            <Button size="sm" onClick={() => { setEditingConta(null); setFormConta({ ...EMPTY_CONTA, tipo: filtroTipo }); setOpenConta(true); }}
              data-testid="cat-nova"><Plus className="w-4 h-4 mr-1" /> Nova categoria</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            {TIPOS.map((t) => (
              <button key={t} type="button" onClick={() => setFiltroTipo(t)} data-testid={`cat-tipo-${t}`}
                className={`px-3 py-1 rounded-full text-xs border ${filtroTipo === t
                  ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'}`}>
                {t}
              </button>
            ))}
            {filtroTipo === 'DESPESA' && ['todas', 'fixa', 'variavel'].map((c) => (
              <button key={c} type="button" onClick={() => setFiltroClasse(c)} data-testid={`cat-classe-${c}`}
                className={`px-3 py-1 rounded-full text-xs border capitalize ${filtroClasse === c
                  ? 'bg-foreground text-background border-foreground' : 'hover:bg-accent'}`}>
                {c}
              </button>
            ))}
            <Input className="w-48 h-8" placeholder="Buscar categoria" value={q}
              onChange={(e) => setQ(e.target.value)} data-testid="cat-busca" />
          </div>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto rounded border">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Código</TableHead><TableHead>Descrição</TableHead>
                <TableHead>CC padrão</TableHead><TableHead>Classe</TableHead><TableHead>Ações</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {!lista.length && (
                  <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                    Nenhuma categoria neste filtro.</TableCell></TableRow>
                )}
                {lista.map((c) => (
                  <TableRow key={c.id} data-testid={`cat-row-${c.cod_conta}`}>
                    <TableCell className="font-mono text-xs">{c.cod_conta}</TableCell>
                    <TableCell>{c.descricao}</TableCell>
                    <TableCell>{c.cc_padrao}</TableCell>
                    <TableCell>
                      {c.tipo === 'DESPESA'
                        ? <Badge variant={c.classe === 'fixa' ? 'default' : 'secondary'}>{c.classe || 'variavel'}</Badge>
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" data-testid={`cat-editar-${c.cod_conta}`}
                          onClick={() => { setEditingConta(c); setFormConta({ ...EMPTY_CONTA, ...c }); setOpenConta(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600"
                          onClick={() => excluirConta(c)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Centros de custo</CardTitle>
          <Button size="sm" onClick={() => { setEditingCC(null); setFormCC({ cod_cc: '', nome_cc: '' }); setOpenCC(true); }}
            data-testid="cc-novo"><Plus className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {!centrosCusto.length && <p className="text-sm text-muted-foreground">Nenhum centro de custo.</p>}
          {centrosCusto.map((c) => (
            <div key={c.id} className="flex items-center justify-between border rounded px-3 py-2"
              data-testid={`cc-row-${c.cod_cc}`}>
              <div>
                <p className="text-sm font-medium">{c.nome_cc}</p>
                <p className="text-xs text-muted-foreground font-mono">{c.cod_cc}</p>
              </div>
              <Button size="sm" variant="ghost" data-testid={`cc-editar-${c.cod_cc}`}
                onClick={() => { setEditingCC(c); setFormCC({ cod_cc: c.cod_cc, nome_cc: c.nome_cc }); setOpenCC(true); }}>
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={openConta} onOpenChange={setOpenConta}>
        <DialogContent data-testid="dialog-categoria">
          <DialogHeader>
            <DialogTitle>{editingConta ? 'Editar categoria' : 'Nova categoria'}</DialogTitle>
            <DialogDescription>O código segue o plano de contas da escola (ex.: 4.1.02.001).</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código *</Label>
              <Input value={formConta.cod_conta} onChange={(e) => setFormConta({ ...formConta, cod_conta: e.target.value })}
                data-testid="cat-cod" /></div>
            <div><Label>Tipo *</Label>
              <Select value={formConta.tipo} onValueChange={(v) => setFormConta({ ...formConta, tipo: v })}>
                <SelectTrigger data-testid="cat-tipo-sel"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="col-span-2"><Label>Descrição *</Label>
              <Input value={formConta.descricao} onChange={(e) => setFormConta({ ...formConta, descricao: e.target.value })}
                data-testid="cat-desc" /></div>
            <div><Label>CC padrão *</Label>
              <Select value={formConta.cc_padrao} onValueChange={(v) => setFormConta({ ...formConta, cc_padrao: v })}>
                <SelectTrigger data-testid="cat-cc"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {centrosCusto.map((c) => <SelectItem key={c.cod_cc} value={c.cod_cc}>{c.nome_cc}</SelectItem>)}
                </SelectContent>
              </Select></div>
            {formConta.tipo === 'DESPESA' && (
              <div><Label>Classe</Label>
                <Select value={formConta.classe || 'variavel'} onValueChange={(v) => setFormConta({ ...formConta, classe: v })}>
                  <SelectTrigger data-testid="cat-classe-sel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixa">Fixa</SelectItem>
                    <SelectItem value="variavel">Variável</SelectItem>
                  </SelectContent>
                </Select></div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpenConta(false)}>Cancelar</Button>
            <Button onClick={salvarConta} data-testid="cat-salvar">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openCC} onOpenChange={setOpenCC}>
        <DialogContent data-testid="dialog-cc">
          <DialogHeader>
            <DialogTitle>{editingCC ? 'Editar centro de custo' : 'Novo centro de custo'}</DialogTitle>
            <DialogDescription>Ex.: PED · Pedagógico, ADM · Administrativo.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código *</Label>
              <Input value={formCC.cod_cc} onChange={(e) => setFormCC({ ...formCC, cod_cc: e.target.value.toUpperCase() })}
                data-testid="cc-cod" /></div>
            <div><Label>Nome *</Label>
              <Input value={formCC.nome_cc} onChange={(e) => setFormCC({ ...formCC, nome_cc: e.target.value })}
                data-testid="cc-nome" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpenCC(false)}>Cancelar</Button>
            <Button onClick={salvarCC} data-testid="cc-salvar">Salvar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriasTab;
